import React, { useState, type ComponentProps } from 'react';
import {
  useDropzone,
  type DropzoneOptions,
  type DropzoneState,
} from 'react-dropzone';
import { CustomErrorMessage } from './error-message';
import { inputVariants } from './input';
import { cn } from './helpers';
import { Trash } from 'iconsax-react';
import { FILE_TYPES } from '@shared/constants/file-types';

interface UploadProps extends DropzoneOptions {
  /**
   * A Custom function that gives you a custom error message when a file upload is rejected.
   * NOTE: if you want the native function use ```onDropRejected``` instead
   * @param customErrorMessage - The custom error message string from the file rejection
   * @returns void
   */
  onRejection?: (customErrorMessage: string) => void;

  /**
   * A Custom function that gives you the uploaded file when a file upload is rejected.
   * NOTE: if you want the native function and convert the upload into controlled component use ```onDrop``` instead
   * @param uploadedFile - The uploaded file
   * @returns void
   */
  onFileChange?: (uploadedFile: File) => void;
  file?: File | null;
  children: React.ReactNode;
}

const UploadContext = React.createContext<
  | (DropzoneState & {
      errorMapper: Record<string, (file: File) => string>;
      file: File | null | undefined;
      setFile: (file: File | null | undefined) => void;
    })
  | null
>(null);

function useUpload() {
  const context = React.useContext(UploadContext);
  if (!context) {
    throw new Error('useUpload must be used within a <Upload />');
  }
  return context;
}

function Upload({
  children,
  file: controlledFile,
  onRejection,
  onFileChange,
  ...options
}: UploadProps) {
  const [file, setFile] = useState<File | null | undefined>(controlledFile);

  const errorMapper = {
    'file-invalid-type': (file: globalThis.File) =>
      `".${file.name.split('.')[1]}" File format is not allowed`,
    'file-too-large': () =>
      `File is larger than ${((options.maxSize || 0) / (1024 * 1024)).toFixed(2)} MB`,
    'too-many-files': () =>
      `Too many files, the maximum number of files is ${options.maxSize}`,
  } as const;

  const dropzone = useDropzone({
    onDrop(droppedFiles) {
      const file = droppedFiles[0];
      setFile(file);
      onFileChange?.(file);
    },

    accept: {
      [FILE_TYPES.jpeg]: [],
      [FILE_TYPES.jpg]: [],
      [FILE_TYPES.png]: [],
    },

    onDropRejected(filesRejected) {
      const fileRejected = filesRejected[0];
      const errorCode = fileRejected.errors[0].code;
      const customErrorMessage =
        errorMapper[errorCode as keyof typeof errorMapper]?.(
          fileRejected.file
        ) || fileRejected.errors[0].message;

      onRejection?.(customErrorMessage);
    },
    ...options,
  });

  return (
    <UploadContext.Provider value={{ ...dropzone, errorMapper, file, setFile }}>
      {children}
    </UploadContext.Provider>
  );
}

const UploadArea = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ ...props }, ref) => {
  const { getRootProps } = useUpload();
  return (
    <div
      ref={ref}
      {...getRootProps()}
      {...props}
    />
  );
});

UploadArea.displayName = 'UploadArea';

const UploadTrigger = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ children, className, ...props }, ref) => {
  const { getInputProps } = useUpload();
  return (
    <div
      className={cn('flex h-[48px]', className)}
      ref={ref}
      {...props}
    >
      {children}
      <input
        type='file'
        {...getInputProps({ name: 'base64' })}
      />
    </div>
  );
});

UploadTrigger.displayName = 'UploadTrigger';

const UploadButton = ({ className, ...props }: ComponentProps<'button'>) => {
  return (
    <button
      type='button'
      className={cn(
        'bg-gray-100 text-gray-800 text-sm flex justify-center items-center hover:bg-gray-200/60 h-full transition-colors cursor-pointer px-4 border rounded-l-lg border-pale-border',
        className
      )}
      {...props}
    >
      Choose File
    </button>
  );
};

const UploadInputDisplay = ({
  placeholder,
  className,
  onFileRemove,
  ...props
}: Omit<ComponentProps<'div'>, 'children'> & {
  placeholder?: string;
  onFileRemove?: () => void;
}) => {
  const { file, setFile } = useUpload();

  return (
    <div
      className={cn(
        inputVariants(),
        'w-full flex items-center text-sm border-l-0 rounded-l-none flex-1 h-full p-4',
        className
      )}
      {...props}
    >
      <span
        className={cn(
          'flex justify-between items-center text-sm w-full text-gray-400 font-normal',
          file && 'text-gray-800'
        )}
      >
        <span className='truncate'>
          {file?.name || placeholder || 'No file choosen'}
        </span>
        {file && (
          <span
            onClick={(e) => {
              e.stopPropagation();
              setFile(null);
              onFileRemove?.();
            }}
            className='text-red-400 hover:text-destructive  cursor-pointer'
          >
            <Trash />
          </span>
        )}
      </span>
    </div>
  );
};

const UploadError = ({
  children,
  ...props
}: ComponentProps<typeof CustomErrorMessage>) => {
  const { fileRejections, errorMapper } = useUpload();

  const errorsSet = new Set();
  const uniqueArray = fileRejections.filter((item) => {
    if (errorsSet.has(JSON.stringify(item.errors))) {
      return false;
    }
    errorsSet.add(JSON.stringify(item.errors));
    return true;
  });

  return uniqueArray.map(({ file, errors }) => {
    return errors.map((err) => (
      <CustomErrorMessage
        key={err.code}
        {...props}
      >
        {children ||
          errorMapper[err.code as keyof typeof errorMapper]?.(file) ||
          err.message}
      </CustomErrorMessage>
    ));
  });
};

export {
  Upload,
  UploadArea,
  UploadButton,
  UploadInputDisplay,
  UploadTrigger,
  UploadError,
};
